import React, { useState, useEffect } from 'react'
import axios from 'axios'
import { 
  Box, 
  TextField, 
  Button, 
  Typography, 
  Alert, 
  Link, 
  LinearProgress,
  Stack,
  IconButton,
  InputAdornment,
  CircularProgress
} from '@mui/material'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import Visibility from '@mui/icons-material/Visibility'
import VisibilityOff from '@mui/icons-material/VisibilityOff'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import ErrorIcon from '@mui/icons-material/Error'
import useRouter from '../hooks/useRouter'
import useSnackbar from '../hooks/useSnackbar'
import { extractErrorMessage } from '../utils/apitools'
import { 
  validatePassword, 
  validatePasswordMatch,
  getPasswordStrengthLabel,
  getPasswordStrengthColor
} from '../utils/passwordValidation'

export const ResetPassword: React.FC = () => {
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [success, setSuccess] = useState(false)
  const [error, setError] = useState('')
  const [pleaId, setPleaId] = useState<string | null>(null)
  const [userId, setUserId] = useState<string | null>(null)
  const [validatingPlea, setValidatingPlea] = useState(false)
  const [pleaValid, setPleaValid] = useState(false)
  
  const snackbar = useSnackbar()
  const router = useRouter()

  // Extract plea ID from URL parameter
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search)
    const aParam = urlParams.get('a')
    
    if (aParam) {
      setPleaId(aParam)
      validatePleaId(aParam)
    } else {
      setError('Invalid reset link. Please request a new password reset.')
    }
  }, [])

  const validatePleaId = async (id: string) => {
    try {
      setValidatingPlea(true)
      const httpResponse: any = await axios.get(`/api/v1/user/plea/${id}`)
      const response = httpResponse.data

      // Handle licensing server response format
      if (response && response.hasError) {
        // Error response: { hasError: true, message: "...", ... }
        setError(response.message || 'Invalid or expired reset link')
        setPleaValid(false)
        return
      }
      
      // Success response: Direct plea object { id, type, data: userId, created }
      if (response && response.type === 'PasswordChange' && response.data) {
        setUserId(response.data) // userId is directly in data field
        setPleaValid(true)
      } else if (response && response.type) {
        setError('Invalid plea type: ' + response.type)
        setPleaValid(false)
      } else {
        setError('Invalid response format')
        setPleaValid(false)
      }
    } catch (err: any) {
      const message = extractErrorMessage(err)
      setError(`Invalid or expired reset link: ${message}`)
      setPleaValid(false)
    } finally {
      setValidatingPlea(false)
    }
  }

  const passwordValidation = validatePassword(password)
  const passwordsMatch = validatePasswordMatch(password, confirmPassword)

  const handleSubmit = async () => {
    if (!pleaId || !pleaValid || !userId) {
      setError('Invalid reset link')
      return
    }

    if (!passwordValidation.isValid) {
      setError('Please fix the password requirements before continuing')
      return
    }

    if (!passwordsMatch) {
      setError('Passwords do not match')
      return
    }

    try {
      setLoading(true)
      setError('')

      const httpResponse: any = await axios.post(`/api/v1/user/password/change`, {
        userId,
        pleaId,
        oldPassword: null,
        newPassword: password
      })
      const response = httpResponse.data
      
      // Check if response has error
      if (response.hasError) {
        setError(response.message || 'Failed to reset password')
        return
      }
      
      setSuccess(true)
      snackbar.success('Password reset successfully! You can now log in with your new password.')
    } catch (err: any) {
      const message = extractErrorMessage(err)
      setError(message)
      snackbar.error(message)
    } finally {
      setLoading(false)
    }
  }

  const handleBackToLogin = () => {
    router.navigate('home')
  }

  const handleRequestNewReset = () => {
    router.navigate('forgot-password')
  }

  if (validatingPlea) {
    return (
      <Box sx={{ display: 'flex', height: 'calc(100vh - 64px)' }}>
        {/* Left section - hidden on small screens */}
        <Box sx={{ width: '50%', bgcolor: 'white', display: { xs: 'none', sm: 'flex' }, alignItems: 'center', justifyContent: 'center' }}>
          <img
            src="/img/platinum-banner.gif"
            alt="Platinum Title Banner"
            style={{ width: '100%', maxWidth: '100%', height: 'auto' }}
          />
        </Box>

        {/* Right section - full width on small screens */}
        <Box sx={{ width: { xs: '100%', sm: '50%' }, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Box sx={{ textAlign: 'center' }}>
            <CircularProgress size={60} sx={{ color: 'info.main', mb: 2 }} />
            <Typography variant="h6">Validating reset link...</Typography>
          </Box>
        </Box>
      </Box>
    )
  }

  if (success) {
    return (
      <Box sx={{ display: 'flex', height: 'calc(100vh - 64px)' }}>
        {/* Left section - hidden on small screens */}
        <Box sx={{ width: '50%', bgcolor: 'white', display: { xs: 'none', sm: 'flex' }, alignItems: 'center', justifyContent: 'center' }}>
          <img
            src="/img/platinum-banner.gif"
            alt="Platinum Title Banner"
            style={{ width: '100%', maxWidth: '100%', height: 'auto' }}
          />
        </Box>

        {/* Right section - full width on small screens */}
        <Box sx={{ width: { xs: '100%', sm: '50%' }, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Box
            sx={{
              maxWidth: 400,
              width: '100%',
              p: 3,
              backgroundColor: 'background.paper',
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
              textAlign: 'center'
            }}
          >
            <CheckCircleIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
            <Typography variant="h4" component="h1" gutterBottom>
              Password Reset Complete
            </Typography>
            <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary' }}>
              Your password has been successfully reset. You can now log in with your new password.
            </Typography>
            <Button
              fullWidth
              variant="contained"
              onClick={handleBackToLogin}
              sx={{ 
                bgcolor: 'info.main', 
                '&:hover': { bgcolor: 'info.dark' },
                color: 'info.contrastText',
                minHeight: '40px',
                fontSize: '0.875rem'
              }}
            >
              Continue to Login
            </Button>
          </Box>
        </Box>
      </Box>
    )
  }

  if (!pleaValid) {
    return (
      <Box sx={{ display: 'flex', height: 'calc(100vh - 64px)' }}>
        {/* Left section - hidden on small screens */}
        <Box sx={{ width: '50%', bgcolor: 'white', display: { xs: 'none', sm: 'flex' }, alignItems: 'center', justifyContent: 'center' }}>
          <img
            src="/img/platinum-banner.gif"
            alt="Platinum Title Banner"
            style={{ width: '100%', maxWidth: '100%', height: 'auto' }}
          />
        </Box>

        {/* Right section - full width on small screens */}
        <Box sx={{ width: { xs: '100%', sm: '50%' }, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Box
            sx={{
              maxWidth: 400,
              width: '100%',
              p: 3,
              backgroundColor: 'background.paper',
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
              textAlign: 'center'
            }}
          >
            <ErrorIcon sx={{ fontSize: 64, color: 'error.main', mb: 2 }} />
            <Typography variant="h4" component="h1" gutterBottom>
              Invalid Reset Link
            </Typography>
            <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary' }}>
              This password reset link is invalid or has expired. Please request a new password reset.
            </Typography>
            <Stack spacing={2}>
              <Button
                fullWidth
                variant="contained"
                onClick={handleRequestNewReset}
                sx={{ 
                  bgcolor: 'info.main', 
                  '&:hover': { bgcolor: 'info.dark' },
                  color: 'info.contrastText',
                  minHeight: '40px',
                  fontSize: '0.875rem'
                }}
              >
                Request New Reset
              </Button>
              <Button
                fullWidth
                variant="outlined"
                onClick={handleBackToLogin}
                sx={{ 
                  borderColor: 'info.main',
                  color: 'info.main',
                  '&:hover': { 
                    borderColor: 'info.dark',
                    bgcolor: 'info.light'
                  },
                  minHeight: '40px',
                  fontSize: '0.875rem'
                }}
              >
                Back to Login
              </Button>
            </Stack>
          </Box>
        </Box>
      </Box>
    )
  }

  return (
    <Box sx={{ display: 'flex', height: 'calc(100vh - 64px)' }}>
      {/* Left section */}
      <Box sx={{ width: '50%', bgcolor: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <img 
          src="/img/platinum-banner.gif" 
          alt="Platinum Title Banner" 
          style={{ width: '100%', maxWidth: '100%', height: 'auto' }} 
        />
      </Box>

      {/* Right section */}
      <Box sx={{ width: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Box
          sx={{
            maxWidth: 400,
            width: '100%',
            p: 3,
            backgroundColor: 'background.paper',
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Box sx={{ mb: 2 }}>
            <Link
              component="button"
              variant="body2"
              onClick={handleBackToLogin}
              sx={{
                color: 'info.main',
                textDecoration: 'none',
                display: 'flex',
                alignItems: 'center',
                '&:hover': {
                  textDecoration: 'underline',
                },
              }}
            >
              <ArrowBackIcon fontSize="small" sx={{ mr: 0.5 }} />
              Back to Login
            </Link>
          </Box>
          
          <Typography variant="h4" component="h1" gutterBottom>
            Set New Password
          </Typography>
          
          <Typography variant="body2" sx={{ mb: 3, color: 'text.secondary' }}>
            Create a strong password for your account. Your password must meet enterprise security requirements.
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box
            component="form"
            onSubmit={(e) => {
              e.preventDefault()
              void handleSubmit()
            }}
          >
            <TextField
              fullWidth
              autoFocus
              autoComplete="new-password"
              type={showPassword ? 'text' : 'password'}
              label="New Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              margin="normal"
              required
              variant="outlined"
              disabled={loading}
              InputLabelProps={{
                shrink: true,
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      size="small"
                      tabIndex={-1}
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
                sx: {
                  height: '40px',
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: (theme) => theme.palette.info.main + '80',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'info.main',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'info.main',
                  },
                },
              }}
            />

            {/* Password strength indicator */}
            {password && (
              <Box sx={{ mt: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    Password Strength
                  </Typography>
                  <Typography variant="caption" sx={{ color: getPasswordStrengthColor(passwordValidation.score) }}>
                    {getPasswordStrengthLabel(passwordValidation.score)}
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={passwordValidation.score}
                  sx={{
                    height: 4,
                    backgroundColor: 'grey.200',
                    '& .MuiLinearProgress-bar': {
                      backgroundColor: getPasswordStrengthColor(passwordValidation.score),
                    },
                  }}
                />
                {passwordValidation.errors.length > 0 && (
                  <Box sx={{ mt: 1 }}>
                    {passwordValidation.errors.map((err, index) => (
                      <Typography key={index} variant="caption" color="error" sx={{ display: 'block' }}>
                        • {err}
                      </Typography>
                    ))}
                  </Box>
                )}
              </Box>
            )}

            <TextField
              fullWidth
              autoComplete="new-password"
              type={showConfirmPassword ? 'text' : 'password'}
              label="Confirm Password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              margin="normal"
              required
              variant="outlined"
              disabled={loading}
              error={confirmPassword.length > 0 && !passwordsMatch}
              helperText={confirmPassword.length > 0 && !passwordsMatch ? 'Passwords do not match' : ''}
              InputLabelProps={{
                shrink: true,
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      edge="end"
                      size="small"
                      tabIndex={-1}
                    >
                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
                sx: {
                  height: '40px',
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: (theme) => theme.palette.info.main + '80',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'info.main',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'info.main',
                  },
                },
              }}
            />
            
            <Button
              fullWidth
              type="submit"
              variant="contained"
              color="info"
              disabled={loading || !passwordValidation.isValid || !passwordsMatch}
              sx={{
                mt: 2,
                minHeight: '40px',
                fontSize: '0.875rem',
              }}
            >
              {loading ? (
                <CircularProgress size={20} sx={{ color: 'info.contrastText' }} />
              ) : (
                'Reset Password'
              )}
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  )
}

export default ResetPassword 